import React, { useEffect, useState } from 'react';
import { Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import { getEventVideosApi, addEventVideoApi, deleteEventVideoApi } from '../../../api/events';

export const EventVideos = ({ eventId, communityId, isOwner }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', url: '' });
  const [submitting, setSubmitting] = useState(false);

  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    if (eventId) fetchVideos();
  }, [eventId]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await getEventVideosApi({ eventId });
      setVideos(res.data || []);
    } catch (err) {
      console.warn('Failed to fetch videos', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await deleteEventVideoApi(id);
      if (playingVideo && playingVideo._id === id) setPlayingVideo(null);
      fetchVideos();
    } catch (err) {
      alert('Failed to delete video.');
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.url) {
      alert('Please provide title and video link');
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('community', communityId);
      fd.append('event', eventId);
      fd.append('title', formData.title);
      fd.append('description', formData.description);
      fd.append('video_link', formData.url);
      await addEventVideoApi(fd);
      setShowAddForm(false);
      setFormData({ title: '', description: '', url: '' });
      fetchVideos();
    } catch (err) {
      alert('Failed to add video.');
    } finally {
      setSubmitting(false);
    }
  };

  if (showAddForm) {
    return (
      <div className="bg-light p-3 rounded-4 border">
        <h6 className="fw-bold mb-3">Add New Video</h6>
        <Form onSubmit={handleAddSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Video title" 
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={2}
              placeholder="Brief description" 
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Video Link</Form.Label>
            <Form.Control 
              type="url" 
              placeholder="Paste YouTube or Vimeo link here"
              value={formData.url}
              onChange={e => setFormData({ ...formData, url: e.target.value })}
              required 
            />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="light" className="rounded-pill" onClick={() => setShowAddForm(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={submitting} className="rounded-pill px-4 cd-btn-orange border-0">
              {submitting ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </Form>
      </div>
    );
  }

  return (
    <div>
      {playingVideo ? (
        <div className="bg-light p-3 rounded-4 border">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <button className="btn btn-link text-dark p-0 me-3" onClick={() => setPlayingVideo(null)}>
                <i className="bi bi-chevron-left fs-5"></i>
              </button>
              <h6 className="fw-bold mb-0 text-truncate">{playingVideo.title}</h6>
            </div>
          </div>
          <div className="ratio ratio-16x9 rounded-3 overflow-hidden bg-dark">
            {(playingVideo.url.includes('youtube.com') || playingVideo.url.includes('vimeo.com') || playingVideo.url.includes('youtu.be')) ? (
              <iframe src={playingVideo.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')} allowFullScreen title="Video"></iframe>
            ) : (
              <video controls src={playingVideo.url} className="w-100 h-100 object-fit-cover"></video>
            )}
          </div>
        </div>
      ) : (
        <>
          {isOwner && (
            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" className="rounded-pill px-4 cd-btn-orange border-0" onClick={() => setShowAddForm(true)}>
                <i className="bi bi-plus-lg me-1"></i> Add Video
              </Button>
            </div>
          )}
          
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : videos.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-film text-muted" style={{ fontSize: '3rem' }}></i>
              <h5 className="mt-3">No Videos Yet</h5>
              <p className="text-muted">No videos have been uploaded.</p>
            </div>
          ) : (
            <Row className="g-3">
              {videos.map((vid) => (
                <Col md={6} key={vid._id}>
                  <div 
                    className="card h-100 border shadow-sm rounded-4 position-relative"
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onClick={() => {
                      const url = vid.video_link || vid.url || vid.video || vid.media;
                      if (url) {
                        setPlayingVideo({ ...vid, url });
                      } else {
                        alert('Video URL not available.');
                      }
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    {isOwner && (
                      <button 
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle shadow-sm"
                        onClick={(e) => { e.stopPropagation(); handleDelete(vid._id); }}
                        title="Delete Video"
                        style={{ zIndex: 2 }}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                    <div className="card-body text-center p-4 d-flex flex-column align-items-center justify-content-center">
                      <div className="bg-warning text-white rounded-circle d-flex align-items-center justify-content-center mb-3 shadow-sm" style={{ width: '50px', height: '50px' }}>
                        <i className="bi bi-play-fill fs-3" style={{ marginLeft: '4px' }}></i>
                      </div>
                      <h6 className="mb-1 fw-bold text-dark">{vid.title}</h6>
                      <small className="text-muted">{vid.createdAt ? new Date(vid.createdAt).toLocaleDateString() : 'No date'}</small>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </div>
  );
};
